import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react'
import type { SaveTarget } from '../../lib/types'
import { LOOSE_LABEL } from '../../lib/takes'
import { useDismiss } from '../../lib/hooks'

/** Past this many collections the menu gets a search box. */
const SEARCH_FROM = 9

interface Props {
  /** What the chip says (sortLabel). */
  label: string
  /** The collections holding the card now; empty = loose. */
  collectionIds: readonly string[]
  /** The reader's collections, most recent first. */
  collections: readonly SaveTarget[]
  open: boolean
  /** A move is on its way to the server. */
  busy: boolean
  onOpen(open: boolean): void
  onSort(collectionId: string | null): void
  /** Resolves false when the collection couldn't be made (the page says why). */
  onCreate(name: string): Promise<boolean>
  /** Beside the chip, on its row (the card's "Open in Burrow"). */
  after?: ReactNode
}

/**
 * One taken card's sorting control: a compact chip ("Loose thoughts", or the
 * collection it's in) that opens a small menu — loose, the reader's
 * collections, or a new one made on the spot. The menu opens inline, under
 * the chip, so it works the same in the desktop drawer and the phone sheet.
 */
export function SortMenu({ label, collectionIds, collections, open, busy, onOpen, onSort, onCreate, after }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const chipRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)

  const close = useCallback(
    (focusChip = false) => {
      onOpen(false)
      setQuery('')
      setCreating(false)
      setName('')
      if (focusChip) chipRef.current?.focus()
    },
    [onOpen],
  )
  const dismiss = useCallback(() => close(), [close])
  useDismiss(wrapRef, open, dismiss)

  const searchable = collections.length >= SEARCH_FROM
  useEffect(() => {
    if (!open) return
    const raf = requestAnimationFrame(() => {
      if (searchRef.current) searchRef.current.focus()
      else (menuRef.current?.querySelector<HTMLElement>('[aria-checked="true"]') ?? firstItem(menuRef.current))?.focus()
    })
    return () => cancelAnimationFrame(raf)
  }, [open])
  useEffect(() => {
    if (creating) nameRef.current?.focus()
  }, [creating])

  const loose = collectionIds.length === 0
  const first = collections.find((c) => c.id === collectionIds[0])
  const q = query.trim().toLowerCase()
  const shown = q ? collections.filter((c) => c.name.toLowerCase().includes(q)) : collections

  const choose = (id: string | null) => {
    onSort(id)
    close(true)
  }

  async function create(e: FormEvent) {
    e.preventDefault()
    const clean = name.trim()
    if (!clean || saving) return
    setSaving(true)
    const ok = await onCreate(clean)
    setSaving(false)
    if (ok) close(true)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!open) return
    if (e.key === 'Escape') {
      // The menu, not the drawer or sheet around it.
      e.preventDefault()
      e.stopPropagation()
      close(true)
      return
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [])
    if (items.length === 0) return
    const i = items.indexOf(document.activeElement as HTMLElement)
    if (i < 0 && document.activeElement !== searchRef.current) return
    e.preventDefault()
    const next = i < 0 ? (e.key === 'ArrowDown' ? 0 : items.length - 1) : (i + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length
    items[next]?.focus()
  }

  const item = 'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-left text-[13px] outline-none hover:bg-foreground/5 focus-visible:bg-foreground/5'
  return (
    <div ref={wrapRef} onKeyDown={onKeyDown} className="flex flex-col gap-2">
      <div className="flex min-h-[26px] items-center justify-between gap-2">
        <button
          ref={chipRef}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          disabled={busy}
          title="Sort this card"
          onClick={() => (open ? close() : onOpen(true))}
          className={`inline-flex min-w-0 items-center gap-1.5 rounded-full border px-2.5 py-[3px] text-[11.5px] font-medium transition-colors disabled:cursor-wait disabled:opacity-60 ${
            open ? 'border-accent bg-card text-foreground' : 'border-border/60 bg-canvas text-foreground/80 hover:border-accent'
          }`}
        >
          <Swatch loose={loose} color={first?.cover_color ?? null} />
          <span className="min-w-0 truncate">{label}</span>
          <svg aria-hidden width="9" height="9" viewBox="0 0 10 10" className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}>
            <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {after}
      </div>

      {open && (
        <div className="flex flex-col gap-0.5 rounded-2xl border border-border/50 bg-card p-1.5 shadow-[var(--shadow-pop)]">
          <span className="px-2.5 pb-1 pt-1 text-[10.5px] font-semibold tracking-[0.06em] text-muted-foreground">SORT INTO</span>
          {searchable && (
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a collection"
              aria-label="Find a collection"
              className="mx-1 mb-1 rounded-lg border border-border/60 bg-canvas px-2.5 py-1.5 text-[13px] text-foreground outline-none placeholder:text-muted-foreground/70 focus:border-accent"
            />
          )}
          <div ref={menuRef} role="menu" aria-label="Sort into" className="flex max-h-[232px] flex-col gap-0.5 overflow-y-auto">
            {!q && (
              <button type="button" role="menuitemradio" aria-checked={loose} onClick={() => choose(null)} className={`${item} ${loose ? 'text-primary' : 'text-foreground'}`}>
                <Swatch loose />
                <span className="min-w-0 flex-1 truncate">{LOOSE_LABEL}</span>
                {loose && <Check />}
              </button>
            )}
            {shown.map((c) => {
              const checked = collectionIds.includes(c.id)
              return (
                <button
                  key={c.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={checked}
                  onClick={() => choose(c.id)}
                  className={`${item} ${checked ? 'text-primary' : 'text-foreground'}`}
                >
                  <Swatch color={c.cover_color} />
                  <span className="min-w-0 flex-1 truncate">{c.name}</span>
                  {checked && <Check />}
                </button>
              )
            })}
            {q && shown.length === 0 && <span className="px-2.5 py-2 text-[13px] text-muted-foreground">No collection by that name.</span>}
            {!creating && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setCreating(true)
                  setName(query.trim())
                }}
                className={`${item} font-medium text-primary`}
              >
                <span aria-hidden className="flex size-2.5 items-center justify-center text-[13px] leading-none">
                  +
                </span>
                New collection…
              </button>
            )}
          </div>
          {creating && (
            <form onSubmit={(e) => void create(e)} className="mx-1 mt-0.5 flex items-center gap-1.5">
              <input
                ref={nameRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                placeholder="Name it"
                aria-label="New collection name"
                className="min-w-0 flex-1 rounded-lg border border-border/80 bg-canvas px-2.5 py-1.5 text-[13px] text-foreground outline-none placeholder:text-muted-foreground/70 focus:border-accent"
              />
              <button
                type="submit"
                disabled={saving || !name.trim()}
                className="rounded-xl bg-accent px-3 py-1.5 text-[13px] font-medium text-accent-foreground hover:opacity-90 disabled:opacity-50"
              >
                {saving ? 'Making…' : 'Create'}
              </button>
            </form>
          )}
          <p className="m-0 px-2.5 pb-1 pt-1.5 text-[10.5px]/[1.45] text-muted-foreground">
            {collectionIds.length > 1
              ? `It’s in ${collectionIds.length} collections. Moving it here takes it out of the others.`
              : 'A card sits in one collection from here: moving it takes it out of any other.'}
          </p>
        </div>
      )}
    </div>
  )
}

function firstItem(menu: HTMLElement | null): HTMLElement | null {
  return menu?.querySelector<HTMLElement>('[role^="menuitem"]') ?? null
}

function Swatch({ loose = false, color = null }: { loose?: boolean; color?: string | null }) {
  if (loose) return <span aria-hidden className="size-2.5 shrink-0 rounded-full border border-dashed border-muted-foreground" />
  return <span aria-hidden className="size-2.5 shrink-0 rounded-[3px]" style={{ background: color || 'var(--accent)' }} />
}

function Check() {
  return (
    <svg aria-hidden width="12" height="12" viewBox="0 0 12 12" className="shrink-0">
      <path d="M2.5 6.2 5 8.5 9.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
